import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  canManage,
  canManageAny,
  changeMemberRole,
  grantable,
  type OrgMemberRow,
  type OrgRole,
} from '../lib/org-members';
import { day } from '../lib/time';
import { Avatar } from './Avatar';
import { RolePill } from './RolePill';
import { buttonClass } from './ui/Button';

// One .row-list roster row (#308): avatar tile + "login — Role · you" lead over
// a muted email/joined line; ghost Remove/Leave on the right (mock SimplePage
// row shape). A real component so `actor`/`busy` arrive as reactive props read
// inside the JSX — a refetch/remount can't bake in a stale `busy=true`. The
// parent owns the mutation + its busy/refetch; this renders and reports.
//
// Remove / Leave only ASK (`onAskRemove`): the parent's confirm sends the
// DELETE (plan 101 row 17-10). Leave renders only for an actor who can manage
// members at all: the DELETE needs `write:members` (owner/admin), so a member's
// or viewer's Leave was a guaranteed 403.

export function MemberRow(props: {
  readonly member: OrgMemberRow;
  readonly actor: OrgRole | null;
  readonly busy: boolean;
  readonly token: string | null | undefined;
  readonly run: (fn: () => Promise<void>, successMsg?: string) => Promise<void>;
  /** Ask the parent to confirm removing (or, on the caller's own row, leaving). */
  readonly onAskRemove: (member: OrgMemberRow) => void;
}): JSX.Element {
  const editable = (): boolean => props.actor !== null && canManage(props.actor, props.member.role);
  const removable = (): boolean =>
    props.member.is_self ? props.actor !== null && canManageAny(props.actor) : editable();
  const options = (): readonly OrgRole[] => (props.actor ? grantable(props.actor) : []);
  return (
    <li class="row-list__row member-row" data-user={props.member.user_id}>
      <div class="row-list__lead">
        <Avatar login={props.member.login} avatar={props.member.avatar} class="row-list__tile" />
        <div class="member-row__id">
          <div class="member-row__name">
            {props.member.login}
            <span class="member-row__sep" aria-hidden="true">
              —
            </span>
            <RolePill
              role={props.member.role}
              editable={editable()}
              options={options()}
              busy={props.busy}
              login={props.member.login}
              onChange={(r) =>
                void props.run(
                  () => changeMemberRole(props.member.user_id, r, props.token),
                  t('org_members.toast_role_changed'),
                )
              }
            />
            <Show when={props.member.is_self}>
              <span class="member-row__self">· {t('org_members.you')}</span>
            </Show>
          </div>
          <div class="member-row__meta">
            <Show when={props.member.email}>
              <span class="member-row__email">{props.member.email}</span>
            </Show>
            <span class="member-row__joined">
              {t('org_members.col_joined')} {day(props.member.joined_iso)}
            </span>
          </div>
        </div>
      </div>
      <Show when={removable()}>
        <button
          type="button"
          class={`${buttonClass({ variant: 'ghost', size: 'sm' })} org-members__remove`}
          disabled={props.busy}
          onClick={() => props.onAskRemove(props.member)}
        >
          {props.member.is_self ? t('org_members.leave') : t('org_members.remove')}
        </button>
      </Show>
    </li>
  );
}
